import { useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const MODULOS_LABELS = {
  pulso:             "Pulso del negocio",
  resumen:           "Resumen",
  estrategico:       "Análisis Estratégico",
  diario:            "Análisis por día",
  tendencia:         "Tendencia diaria",
  transportadoras:   "Transportadoras",
  stock:             "Stock Evolutivo",
  "venta-logistica": "Venta y Logística",
  "detalle-producto":"Detalle por Producto",
  calculadora:       "Calculadora",
  carga:             "Cargar archivos",
  "carga-stock":     "Snapshot de stock",
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

function ModalUsuario({ usuario, onClose, onSave }) {
  const esNuevo = !usuario;
  const [form, setForm] = useState({
    nombre:   usuario?.nombre   || "",
    username: usuario?.username || "",
    email:    usuario?.email    || "",
    password: "",
    rol:      usuario?.rol      || "usuario",
    plan:     usuario?.plan     || "free",
    activo:   usuario?.activo   ?? 1,
  });
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const body = { ...form };
      if (!body.password) delete body.password;

      const url = esNuevo
        ? `${API_URL}/api/admin/usuarios`
        : `${API_URL}/api/admin/usuarios/${usuario.id}`;
      const method = esNuevo ? "POST" : "PUT";

      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al guardar");
      }
      onSave();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "8px 10px", borderRadius: 7,
    border: "0.5px solid #ddd", fontSize: 13, outline: "none",
    background: "#fafaf9", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "1.75rem 2rem", width: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{esNuevo ? "Nuevo usuario" : "Editar usuario"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Nombre</label>
              <input style={inputStyle} value={form.nombre} onChange={e => set("nombre", e.target.value)} required />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Usuario</label>
              <input style={inputStyle} value={form.username} onChange={e => set("username", e.target.value)} required disabled={!esNuevo} />
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Email</label>
            <input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} required />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>
              Contraseña{" "}
              <span style={{ color: "#aaa" }}>
                {esNuevo ? "(dejar vacío si usará Google para ingresar)" : "(dejar vacío para no cambiar)"}
              </span>
            </label>
            <input style={inputStyle} type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="••••••••" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Rol</label>
              <select style={inputStyle} value={form.rol} onChange={e => set("rol", e.target.value)}>
                <option value="usuario">Usuario</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Plan</label>
              <select style={inputStyle} value={form.plan} onChange={e => set("plan", e.target.value)}>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
              </select>
            </div>
          </div>

          {!esNuevo && (
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ fontSize: 11, color: "#666", display: "block", marginBottom: 4 }}>Estado</label>
              <select style={inputStyle} value={form.activo} onChange={e => set("activo", parseInt(e.target.value))}>
                <option value={1}>Activo</option>
                <option value={0}>Desactivado</option>
              </select>
            </div>
          )}
          {esNuevo && <div style={{ marginBottom: "1.25rem" }} />}

          {error && (
            <div style={{ background: "#FCEBEB", color: "#A32D2D", fontSize: 12, padding: "8px 10px", borderRadius: 7, marginBottom: "1rem" }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "0.5px solid #ddd", background: "transparent", fontSize: 13, cursor: "pointer" }}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ModalModulos({ usuario, onClose, onSave }) {
  const [modulos, setModulos] = useState(
    Object.fromEntries(
      Object.keys(MODULOS_LABELS).map(k => [k, usuario.modulos?.[k] ?? true])
    )
  );
  const [loading, setLoading] = useState(false);

  const toggle = k => setModulos(prev => ({ ...prev, [k]: !prev[k] }));

  const handleSave = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/admin/usuarios/${usuario.id}/modulos`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ modulos }),
      });
      onSave();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "1.75rem 2rem", width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.1rem" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Módulos — {usuario.nombre}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>

        <p style={{ fontSize: 12, color: "#888", margin: "0 0 1rem" }}>
          Activa o desactiva las secciones que este usuario puede ver.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: "1.25rem" }}>
          {Object.entries(MODULOS_LABELS).map(([key, label]) => {
            const activo = modulos[key];
            return (
              <div
                key={key}
                onClick={() => toggle(key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderRadius: 9, cursor: "pointer",
                  background: activo ? "#f4fbf8" : "#fafaf9",
                  border: activo ? "1px solid #1D9E7550" : "0.5px solid #e8e8e6",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: activo ? 500 : 400, color: activo ? "#111" : "#888" }}>{label}</span>
                <div style={{
                  width: 34, height: 19, borderRadius: 10, position: "relative",
                  background: activo ? "#1D9E75" : "#ddd", transition: "background 0.2s",
                }}>
                  <div style={{
                    position: "absolute", top: 3, left: activo ? 17 : 3,
                    width: 13, height: 13, borderRadius: "50%", background: "#fff",
                    transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }} />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "0.5px solid #ddd", background: "transparent", fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            {loading ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CodigosActivacion() {
  const [codigos,   setCodigos]   = useState([]);
  const [cantidad,  setCantidad]  = useState(1);
  const [plan,      setPlan]      = useState("pro");
  const [loading,   setLoading]   = useState(false);
  const [copiado,   setCopiado]   = useState(null);

  const cargar = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/admin/codigos`, { headers: authHeaders() });
    if (res.ok) setCodigos(await res.json());
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const generar = async () => {
    setLoading(true);
    await fetch(`${API_URL}/api/admin/codigos`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ plan, cantidad }),
    });
    await cargar();
    setLoading(false);
  };

  const eliminar = async (codigo) => {
    await fetch(`${API_URL}/api/admin/codigos/${codigo}`, {
      method: "DELETE", headers: authHeaders(),
    });
    cargar();
  };

  const copiar = (codigo) => {
    navigator.clipboard.writeText(codigo);
    setCopiado(codigo);
    setTimeout(() => setCopiado(null), 2000);
  };

  const inputStyle = { padding: "6px 10px", borderRadius: 7, border: "0.5px solid #ddd", fontSize: 13, background: "#fafaf9", outline: "none" };

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1.25rem 1.5rem", marginTop: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1E1E2E" }}>Códigos de activación</div>
        <div style={{ fontSize: 12, color: "#999", marginTop: 3 }}>
          Genera códigos para que tus clientes activen su plan Pro al recibirlos.
        </div>
      </div>

      {/* Generador */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap" }}>
        <select style={inputStyle} value={plan} onChange={e => setPlan(e.target.value)}>
          <option value="pro">Plan Pro</option>
        </select>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "#888" }}>Cantidad:</span>
          <input
            type="number" min={1} max={50} value={cantidad}
            onChange={e => setCantidad(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
            style={{ ...inputStyle, width: 60, textAlign: "center" }}
          />
        </div>
        <button
          onClick={generar}
          disabled={loading}
          style={{ padding: "6px 18px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
        >
          {loading ? "Generando..." : "Generar"}
        </button>
      </div>

      {/* Tabla de códigos */}
      {codigos.length === 0 ? (
        <div style={{ fontSize: 13, color: "#aaa", textAlign: "center", padding: "1rem 0" }}>
          No hay códigos generados aún.
        </div>
      ) : (
        <div style={{ border: "0.5px solid #eee", borderRadius: 9, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.8fr 0.6fr 0.8fr 1.4fr 0.8fr", background: "#fafaf9", borderBottom: "0.5px solid #eee" }}>
            {["Código", "Plan", "Estado", "Usado por", "Acciones"].map(h => (
              <div key={h} style={{ fontSize: 10, color: "#aaa", padding: "8px 12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
            ))}
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {codigos.map((c, i) => (
              <div key={c.codigo} style={{
                display: "grid", gridTemplateColumns: "1.8fr 0.6fr 0.8fr 1.4fr 0.8fr",
                alignItems: "center", borderBottom: i < codigos.length - 1 ? "0.5px solid #f5f5f4" : "none",
                background: i % 2 === 0 ? "#fff" : "#fafaf9",
              }}>
                <div style={{ padding: "9px 12px", fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "#1E1E2E", letterSpacing: "0.05em" }}>
                  {c.codigo}
                </div>
                <div style={{ padding: "9px 12px" }}>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "#EEF2FF", color: "#4338CA", fontWeight: 600 }}>
                    {c.plan?.toUpperCase()}
                  </span>
                </div>
                <div style={{ padding: "9px 12px" }}>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 500,
                    background: c.usado ? "#FCEBEB" : "#EAF3DE",
                    color: c.usado ? "#A32D2D" : "#3B6D11",
                  }}>
                    {c.usado ? "Usado" : "Disponible"}
                  </span>
                </div>
                <div style={{ padding: "9px 12px", fontSize: 12, color: "#666" }}>
                  {c.usado_por_email || "—"}
                </div>
                <div style={{ padding: "9px 12px", display: "flex", gap: 6 }}>
                  <button
                    onClick={() => copiar(c.codigo)}
                    style={{ fontSize: 11, padding: "3px 9px", borderRadius: 6, border: "0.5px solid #ddd", background: copiado === c.codigo ? "#EAF3DE" : "transparent", cursor: "pointer", color: copiado === c.codigo ? "#3B6D11" : "#555" }}
                  >
                    {copiado === c.codigo ? "✓" : "Copiar"}
                  </button>
                  {!c.usado && (
                    <button
                      onClick={() => eliminar(c.codigo)}
                      style={{ fontSize: 11, padding: "3px 9px", borderRadius: 6, border: "0.5px solid #E24B4A30", background: "#E24B4A10", cursor: "pointer", color: "#E24B4A" }}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


const TIER_CONFIG = {
  free:     { label: "Free",        bg: "#f0fdf4", border: "1px solid #86efac", color: "#166534", badgeBg: "#bbf7d0" },
  pro:      { label: "Pro",         bg: "#EEF2FF", border: "1px solid #818CF8", color: "#4338CA", badgeBg: "#C7D2FE" },
  disabled: { label: "Desactivado", bg: "#fafaf9", border: "0.5px solid #ddd",  color: "#aaa",    badgeBg: "#e8e8e6" },
};

const TIER_CYCLE = { free: "pro", pro: "disabled", disabled: "free" };

function PlanConfig() {
  const [config, setConfig] = useState([]);
  const [saving, setSaving] = useState(null);

  const cargar = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/admin/plan-config`, { headers: authHeaders() });
    if (res.ok) setConfig(await res.json());
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarTier = async (modulo, tierActual) => {
    const nuevoTier = TIER_CYCLE[tierActual] || "pro";
    setSaving(modulo);
    await fetch(`${API_URL}/api/admin/plan-config/${modulo}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ tier: nuevoTier }),
    });
    await cargar();
    setSaving(null);
  };

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1.25rem 1.5rem", marginTop: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1E1E2E" }}>Configuración de módulos</div>
        <div style={{ fontSize: 12, color: "#999", marginTop: 3 }}>
          Haz clic en un módulo para cambiar su estado. <strong>Desactivado</strong> = no visible para ningún usuario.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
        {config.map(({ modulo, tier }) => {
          const label = MODULOS_LABELS[modulo] || modulo;
          const busy  = saving === modulo;
          const t     = TIER_CONFIG[tier] || TIER_CONFIG.disabled;
          return (
            <div
              key={modulo}
              onClick={() => !busy && cambiarTier(modulo, tier)}
              title={`Clic para cambiar → ${TIER_CONFIG[TIER_CYCLE[tier]]?.label}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", borderRadius: 9, cursor: busy ? "wait" : "pointer",
                background: t.bg, border: t.border, transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: t.color }}>{label}</span>
              <span style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
                background: t.badgeBg, color: t.color, minWidth: 32, textAlign: "center",
              }}>
                {busy ? "..." : t.label}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 16, marginTop: "0.85rem", flexWrap: "wrap" }}>
        {Object.entries(TIER_CONFIG).map(([key, t]) => (
          <span key={key} style={{ fontSize: 11, color: "#888", display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: t.badgeBg, border: `1px solid ${t.color}`, display: "inline-block" }} />
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}


const TIPO_LABELS = {
  login:          { label: "Login",        bg: "#EAF3DE", color: "#3B6D11" },
  navegacion:     { label: "Vista",        bg: "#EEF2FF", color: "#4338CA" },
  carga_archivos: { label: "Carga",        bg: "#FEF9EC", color: "#7D5A00" },
  logout:         { label: "Logout",       bg: "#F5F5F4", color: "#666"    },
};

const VISTA_LABELS = {
  pulso: "Pulso del negocio", resumen: "Resumen", estrategico: "Análisis Estratégico",
  diario: "Análisis por día", tendencia: "Tendencia diaria", transportadoras: "Transportadoras",
  stock: "Stock Evolutivo", "venta-logistica": "Venta y Logística",
  "detalle-producto": "Detalle por Producto", calculadora: "Calculadora",
  carga: "Cargar archivos", "carga-stock": "Snapshot de stock", admin: "Administración",
};

function formatFecha(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("es-CL", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}

function ActivityLog({ usuarios }) {
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filtroUser, setFiltroUser] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");

  const cargarLogs = useCallback(async (uid = null) => {
    setLoading(true);
    try {
      const qs = uid ? `?usuario_id=${uid}` : "";
      const res = await fetch(`${API_URL}/api/admin/logs${qs}`, { headers: authHeaders() });
      if (res.ok) setLogs(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarLogs(); }, [cargarLogs]);

  const handleFiltroUser = (uid) => {
    setFiltroUser(uid);
    cargarLogs(uid || null);
  };

  const logsFiltrados = filtroTipo
    ? logs.filter(l => l.tipo === filtroTipo)
    : logs;

  const detalleLegible = (tipo, detalle) => {
    if (tipo === "navegacion") return VISTA_LABELS[detalle] || detalle;
    return detalle || "—";
  };

  const select = {
    padding: "5px 10px", fontSize: 12, borderRadius: 7,
    border: "0.5px solid #e0e0de", background: "#fafaf9",
    color: "#444", cursor: "pointer", outline: "none",
  };

  return (
    <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, overflow: "hidden", marginTop: "1rem" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "0.5px solid #f0f0ee", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1E1E2E" }}>Registro de actividad</div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>Últimos {logs.length} eventos</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Filtro usuario */}
          <select style={select} value={filtroUser} onChange={e => handleFiltroUser(e.target.value)}>
            <option value="">Todos los usuarios</option>
            {usuarios.map(u => (
              <option key={u.id} value={u.id}>{u.nombre} ({u.username})</option>
            ))}
          </select>
          {/* Filtro tipo */}
          <select style={select} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            <option value="login">Login</option>
            <option value="navegacion">Navegación</option>
            <option value="carga_archivos">Carga de archivos</option>
          </select>
          <button
            onClick={() => cargarLogs(filtroUser || null)}
            style={{ ...select, background: "#4338CA", color: "#fff", border: "none", padding: "5px 14px" }}
          >
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* Cabecera tabla */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 2fr 1.2fr", background: "#fafaf9", borderBottom: "0.5px solid #eee" }}>
        {["Fecha", "Usuario", "Tipo", "Detalle", "IP"].map(h => (
          <div key={h} style={{ fontSize: 10, color: "#aaa", padding: "9px 14px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
        ))}
      </div>

      {/* Filas */}
      <div style={{ maxHeight: 480, overflowY: "auto" }}>
        {loading && (
          <div style={{ padding: "2rem", textAlign: "center", color: "#aaa", fontSize: 13 }}>Cargando...</div>
        )}
        {!loading && logsFiltrados.length === 0 && (
          <div style={{ padding: "2rem", textAlign: "center", color: "#aaa", fontSize: 13 }}>Sin registros</div>
        )}
        {!loading && logsFiltrados.map((log, i) => {
          const tipo = TIPO_LABELS[log.tipo] || { label: log.tipo, bg: "#f5f5f4", color: "#666" };
          return (
            <div
              key={log.id}
              style={{
                display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 2fr 1.2fr",
                alignItems: "center",
                borderBottom: i < logsFiltrados.length - 1 ? "0.5px solid #f5f5f4" : "none",
                background: i % 2 === 0 ? "#fff" : "#fafaf9",
              }}
            >
              <div style={{ padding: "9px 14px", fontSize: 12, color: "#555" }}>{formatFecha(log.created_at)}</div>
              <div style={{ padding: "9px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#222" }}>{log.nombre || "—"}</div>
                <div style={{ fontSize: 10, color: "#aaa" }}>{log.username || ""}</div>
              </div>
              <div style={{ padding: "9px 14px" }}>
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 500, background: tipo.bg, color: tipo.color }}>
                  {tipo.label}
                </span>
              </div>
              <div style={{ padding: "9px 14px", fontSize: 12, color: "#444" }}>{detalleLegible(log.tipo, log.detalle)}</div>
              <div style={{ padding: "9px 14px", fontSize: 11, color: "#aaa", fontFamily: "monospace" }}>{log.ip || "—"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Funnel Dashboard ─────────────────────────────────────────────────────────

const RANGOS = [
  { key: "7",   label: "7 días"  },
  { key: "30",  label: "30 días" },
  { key: "90",  label: "90 días" },
];

function fechaISO(daysAgo = 0) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function FunnelKpi({ label, value, sub, color = "#111" }) {
  return (
    <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, marginTop: 6, letterSpacing: "-0.02em" }}>
        {value.toLocaleString("es-CL")}
      </div>
      {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function FunnelTasa({ label, value, from, to }) {
  const color = value >= 5 ? "#16A34A" : value >= 1 ? "#D97706" : "#888";
  return (
    <div style={{ background: "#fafaf9", border: "0.5px solid #eee", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 10, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color, marginTop: 4 }}>
        {value.toFixed(1)}%
      </div>
      <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
        {from} → {to}
      </div>
    </div>
  );
}

function FunnelDashboard() {
  const [rango, setRango] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const desde = fechaISO(Number(rango));
      const hasta = fechaISO(0);
      const res = await fetch(
        `${API_URL}/api/admin/funnel?desde=${desde}&hasta=${hasta}`,
        { headers: authHeaders() }
      );
      if (res.ok) setData(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [rango]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div style={{ marginBottom: "2rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Funnel de conversión</h2>
          <p style={{ fontSize: 12, color: "#888", margin: "4px 0 0" }}>
            De visitante anónimo a cliente Pro pagando
          </p>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {RANGOS.map(r => (
            <button
              key={r.key}
              onClick={() => setRango(r.key)}
              style={{
                fontSize: 12, padding: "6px 12px", borderRadius: 7,
                border: "0.5px solid",
                borderColor: rango === r.key ? "#111" : "#ddd",
                background: rango === r.key ? "#111" : "#fff",
                color: rango === r.key ? "#fff" : "#555",
                cursor: "pointer", fontWeight: 500,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "3rem", textAlign: "center", color: "#888", fontSize: 13 }}>
          Cargando...
        </div>
      )}

      {!loading && data && (
        <>
          {/* 5 KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 12 }}>
            <FunnelKpi label="Visitas"        value={data.visitas}        color="#0F172A" />
            <FunnelKpi label="Únicas"         value={data.visitas_unicas} color="#0F172A" sub="por sesión" />
            <FunnelKpi label="Signups Free"   value={data.signups_free}   color="#16A34A" />
            <FunnelKpi label="Clicks Pago"    value={data.clicks_pago}    color="#D97706" />
            <FunnelKpi label="Pagados (Pro)"  value={data.pagos}          color="#4338CA" />
          </div>

          {/* Tasas de conversión */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
            <FunnelTasa label="Visita → Signup" value={data.tasa_visita_signup} from="únicas" to="free" />
            <FunnelTasa label="Signup → Click"  value={data.tasa_signup_click}  from="free"   to="click pago" />
            <FunnelTasa label="Click → Pago"    value={data.tasa_click_pago}    from="click"  to="pago" />
            <FunnelTasa label="Visita → Pago"   value={data.tasa_visita_pago}   from="única"  to="pago (overall)" />
          </div>

          {/* Gráfico temporal */}
          <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1rem 0.5rem" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, paddingLeft: 8 }}>
              Evolución diaria
            </div>
            {data.serie_diaria.length === 0 ? (
              <div style={{ padding: "2.5rem", textAlign: "center", color: "#aaa", fontSize: 13 }}>
                Sin datos en este rango
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.serie_diaria} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#888" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#888" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="visitas"     stroke="#0F172A" strokeWidth={2} dot={false} name="Visitas" />
                  <Line type="monotone" dataKey="unicas"      stroke="#64748B" strokeWidth={2} dot={false} name="Únicas" />
                  <Line type="monotone" dataKey="signups"     stroke="#16A34A" strokeWidth={2} dot={false} name="Signups" />
                  <Line type="monotone" dataKey="clicks_pago" stroke="#D97706" strokeWidth={2} dot={false} name="Clicks pago" />
                  <Line type="monotone" dataKey="pagos"       stroke="#4338CA" strokeWidth={2} dot={false} name="Pagos" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}


export default function AdminPanel() {
  const [usuarios, setUsuarios]         = useState([]);
  const [modalUsuario, setModalUsuario] = useState(null); // null | false (nuevo) | objeto (editar)
  const [modalModulos, setModalModulos] = useState(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState(null);

  const cargar = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/admin/usuarios`, { headers: authHeaders() });
    if (res.ok) setUsuarios(await res.json());
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async id => {
    await fetch(`${API_URL}/api/admin/usuarios/${id}`, { method: "DELETE", headers: authHeaders() });
    setConfirmarEliminar(null);
    cargar();
  };

  const cambiarPlan = async (uid, plan) => {
    await fetch(`${API_URL}/api/admin/usuarios/${uid}/plan`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ plan }),
    });
    cargar();
  };

  const rolBadge = rol => (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 500,
      background: rol === "admin" ? "#EAF3DE" : "#f0f0ee",
      color: rol === "admin" ? "#3B6D11" : "#666",
    }}>{rol === "admin" ? "Admin" : "Usuario"}</span>
  );

  const activoBadge = activo => (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 20,
      background: activo ? "#EAF3DE" : "#FCEBEB",
      color: activo ? "#3B6D11" : "#A32D2D",
    }}>{activo ? "Activo" : "Inactivo"}</span>
  );

  const planBadge = plan => (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
      background: plan === "pro" ? "#EEF2FF" : "#F5F5F4",
      color: plan === "pro" ? "#4338CA" : "#888",
    }}>{plan === "pro" ? "Pro" : "Free"}</span>
  );

  return (
    <div>
      {/* Funnel de conversión */}
      <FunnelDashboard />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Panel de administración</h2>
          <p style={{ fontSize: 13, color: "#888", margin: "4px 0 0" }}>Gestiona usuarios y permisos de módulos</p>
        </div>
        <button
          onClick={() => setModalUsuario(false)}
          style={{ fontSize: 13, padding: "8px 18px", borderRadius: 9, border: "none", background: "#111", color: "#fff", fontWeight: 500, cursor: "pointer" }}
        >
          + Nuevo usuario
        </button>
      </div>

      {/* Tabla de usuarios */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, overflow: "hidden" }}>
        {/* Cabecera */}
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1.5fr 0.7fr 0.6fr 0.7fr 1.2fr", background: "#fafaf9", borderBottom: "0.5px solid #eee" }}>
          {["Nombre / Email", "Usuario", "Rol", "Plan", "Estado", "Acciones"].map(h => (
            <div key={h} style={{ fontSize: 10, color: "#aaa", padding: "10px 14px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
          ))}
        </div>

        {usuarios.length === 0 && (
          <div style={{ padding: "2rem", textAlign: "center", color: "#aaa", fontSize: 13 }}>Sin usuarios</div>
        )}

        {usuarios.map((u, i) => (
          <div
            key={u.id}
            style={{
              display: "grid", gridTemplateColumns: "1.5fr 1.5fr 0.7fr 0.6fr 0.7fr 1.2fr",
              borderBottom: i < usuarios.length - 1 ? "0.5px solid #f5f5f4" : "none",
              alignItems: "center",
            }}
          >
            <div style={{ padding: "12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{u.nombre}</div>
              <div style={{ fontSize: 11, color: "#aaa" }}>{u.email}</div>
            </div>
            <div style={{ padding: "12px 14px", fontSize: 12, color: "#555" }}>{u.username}</div>
            <div style={{ padding: "12px 14px" }}>{rolBadge(u.rol)}</div>
            <div style={{ padding: "12px 14px" }}>
              {planBadge(u.plan || "free")}
            </div>
            <div style={{ padding: "12px 14px" }}>{activoBadge(u.activo)}</div>
            <div style={{ padding: "12px 14px", display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button
                onClick={() => setModalUsuario(u)}
                style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: "0.5px solid #ddd", background: "transparent", cursor: "pointer", color: "#555" }}
              >
                Editar
              </button>
              <button
                onClick={() => setModalModulos(u)}
                style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: "0.5px solid #534AB730", background: "#534AB710", cursor: "pointer", color: "#534AB7" }}
              >
                Módulos
              </button>
              {u.rol !== "admin" && (
                <button
                  onClick={() => cambiarPlan(u.id, u.plan === "pro" ? "free" : "pro")}
                  title={u.plan === "pro" ? "Bajar a Free" : "Subir a Pro"}
                  style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: "0.5px solid #4338CA30", background: "#4338CA10", cursor: "pointer", color: "#4338CA" }}
                >
                  {u.plan === "pro" ? "→ Free" : "→ Pro"}
                </button>
              )}
              {u.rol !== "admin" && (
                <button
                  onClick={() => setConfirmarEliminar(u)}
                  style={{ fontSize: 11, padding: "4px 10px", borderRadius: 7, border: "0.5px solid #E24B4A30", background: "#E24B4A10", cursor: "pointer", color: "#E24B4A" }}
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Configuración de planes */}
      <PlanConfig />
      <CodigosActivacion />

      {/* Log de actividad */}
      <ActivityLog usuarios={usuarios} />

      {/* Modales */}
      {(modalUsuario === false || modalUsuario?.id) && (
        <ModalUsuario
          usuario={modalUsuario || null}
          onClose={() => setModalUsuario(null)}
          onSave={() => { setModalUsuario(null); cargar(); }}
        />
      )}
      {modalModulos && (
        <ModalModulos
          usuario={modalModulos}
          onClose={() => setModalModulos(null)}
          onSave={() => { setModalModulos(null); cargar(); }}
        />
      )}

      {/* Confirmar eliminación */}
      {confirmarEliminar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem 2rem", width: 340, boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>¿Eliminar usuario?</h3>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 1.25rem" }}>
              Se eliminará <strong>{confirmarEliminar.nombre}</strong> y todo su acceso al sistema. Esta acción no se puede deshacer.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmarEliminar(null)} style={{ padding: "8px 18px", borderRadius: 8, border: "0.5px solid #ddd", background: "transparent", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={() => eliminar(confirmarEliminar.id)} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#E24B4A", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
